import { useEffect, useMemo, useState } from "react";
import {
  createEmployeeTask,
  getEmployeeTasks,
  updateEmployeeTaskStatus,
} from "../api/instateamTasksApi";
import { AppIcon } from "../components/InstateamEmployeeComponents";
import {
  getPresenceStatusMeta,
  resolveEmployeeProfileStatus,
} from "../utils/employeeHelpers";
import {
  addDays,
  buildTaskForm,
  formatTaskDate,
  formatTaskLabel,
  formatTaskMonth,
  getWeekDays,
  matchesTaskSearch,
  todayKey,
} from "../utils/employeeTaskHelpers";

const EMPTY_TASK_FORM = buildTaskForm({}, todayKey());

export default function EmployeeTasksPage({ token, searchText = "" }) {
  const [selectedDate, setSelectedDate] = useState(todayKey());
  const [activeTab, setActiveTab] = useState("mine");
  const [tasks, setTasks] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [missedDates, setMissedDates] = useState([]);
  const [missedTasks, setMissedTasks] = useState([]);
  const [dateWindow, setDateWindow] = useState({
    minDate: addDays(todayKey(), -3),
    maxDate: addDays(todayKey(), 1),
    today: todayKey(),
  });
  const [taskPermission, setTaskPermission] = useState({
    canAssignTasks: false,
    assignableEmployees: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [taskForm, setTaskForm] = useState(EMPTY_TASK_FORM);
  const [isSavingTask, setIsSavingTask] = useState(false);
  const [isUpdatingTaskId, setIsUpdatingTaskId] = useState("");

  async function fetchTasks(nextDate = selectedDate, nextTab = activeTab) {
    if (!token) return;

    try {
      setIsLoading(true);
      setStatusMessage(null);
      const payload = await getEmployeeTasks(token, {
        date: nextDate,
        tab: nextTab,
      });

      setTasks(payload.tasks || []);
      setMetrics(payload.metrics || {});
      setMissedDates(payload.missedDates || []);
      setMissedTasks(payload.missedTasks || []);
      setDateWindow(payload.dateWindow || dateWindow);
      setTaskPermission(payload.taskPermission || { canAssignTasks: false, assignableEmployees: [] });
    } catch (error) {
      setStatusMessage({ type: "error", text: error.message });
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedDate, activeTab]);

  const visibleTasks = useMemo(
    () => tasks.filter((task) => matchesTaskSearch(task, searchText)),
    [searchText, tasks]
  );

  const missedDateSet = new Set(missedDates);
  const weekDays = getWeekDays(selectedDate);

  function selectDate(dateKey) {
    if (dateKey < dateWindow.minDate || dateKey > dateWindow.maxDate) {
      setStatusMessage({
        type: "warning",
        text: "You can view one future date and the previous three dates only.",
      });
      return;
    }

    setSelectedDate(dateKey);
  }

  function openAssignTask() {
    setTaskForm(buildTaskForm({}, selectedDate));
    setIsAssignModalOpen(true);
  }

  function closeAssignModal() {
    if (isSavingTask) return;
    setIsAssignModalOpen(false);
    setTaskForm(buildTaskForm({}, selectedDate));
  }

  async function submitAssignedTask(event) {
    event.preventDefault();

    if (!taskForm.title.trim() || !taskForm.details.trim() || !taskForm.assignedTo) {
      setStatusMessage({
        type: "warning",
        text: "Task title, details, and assigned employee are required.",
      });
      return;
    }

    try {
      setIsSavingTask(true);
      await createEmployeeTask(token, {
        ...taskForm,
        estimatedHours: Number(taskForm.estimatedHours || 0),
        estimatedExtraMinutes: Number(taskForm.estimatedExtraMinutes || 0),
      });
      setStatusMessage({ type: "success", text: "Task assigned successfully." });
      closeAssignModal();
      setActiveTab("assigned");
      await fetchTasks(selectedDate, "assigned");
    } catch (error) {
      setStatusMessage({ type: "error", text: error.message });
    } finally {
      setIsSavingTask(false);
    }
  }

  async function updateTaskStatus(task, status) {
    try {
      setIsUpdatingTaskId(task._id);
      const payload = await updateEmployeeTaskStatus(token, task._id, status);
      setTasks((current) =>
        current.map((item) => (item._id === task._id ? payload.task : item))
      );
      setSelectedTask(payload.task);
      setStatusMessage({ type: "success", text: "Task status updated." });
      await fetchTasks(selectedDate, activeTab);
    } catch (error) {
      setStatusMessage({ type: "error", text: error.message });
    } finally {
      setIsUpdatingTaskId("");
    }
  }

  function exportTasks() {
    const headers = [
      "Title",
      "Project",
      "Assigned By",
      "Assigned To",
      "Priority",
      "Estimated Time",
      "Due Date",
      "Status",
    ];
    const rows = visibleTasks.map((task) => [
      task.title,
      task.project || "General",
      task.assignedBy?.fullName || "",
      task.assignedTo?.fullName || "",
      formatTaskLabel(task.priority),
      task.estimatedTimeLabel,
      formatTaskDate(task.dueDate),
      formatTaskLabel(task.effectiveStatus),
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((value) => `"${String(value || "").replaceAll('"', '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `instateam-tasks-${selectedDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <section className="employee-task-page">
      <div className="employee-task-page-head">
        <div>
          <h1>My Tasks</h1>
          <p>Dashboard &gt; Tasks</p>
        </div>
        <div className="employee-task-actions">
          {taskPermission.canAssignTasks ? (
            <button className="primary-button" type="button" onClick={openAssignTask}>
              <AppIcon name="plus" /> Assign Task
            </button>
          ) : null}
          <button className="secondary-button" type="button" onClick={exportTasks}>
            <AppIcon name="download" /> Export
          </button>
        </div>
      </div>

      <div className="employee-task-tabs">
        <button className={activeTab === "mine" ? "active" : ""} type="button" onClick={() => setActiveTab("mine")}>
          My Tasks
        </button>
        {taskPermission.canAssignTasks ? (
          <button className={activeTab === "assigned" ? "active" : ""} type="button" onClick={() => setActiveTab("assigned")}>
            Task I Assigned
          </button>
        ) : null}
      </div>

      <div className="employee-task-date-strip">
        <div className="employee-task-date-head">
          <button type="button" onClick={() => selectDate(addDays(selectedDate, -1))} disabled={selectedDate <= dateWindow.minDate}>
            <AppIcon name="chevronLeft" />
          </button>
          <strong>{formatTaskMonth(selectedDate)}</strong>
          <button type="button" onClick={() => selectDate(addDays(selectedDate, 1))} disabled={selectedDate >= dateWindow.maxDate}>
            <AppIcon name="chevronRight" />
          </button>
          <button type="button" onClick={() => selectDate(dateWindow.today || todayKey())}>
            Today
          </button>
          <input
            type="date"
            min={dateWindow.minDate}
            max={dateWindow.maxDate}
            value={selectedDate}
            onChange={(event) => selectDate(event.target.value)}
          />
        </div>
        <div className="employee-task-week-grid">
          {weekDays.map((day) => {
            const isDisabled = day.key < dateWindow.minDate || day.key > dateWindow.maxDate;
            return (
              <button
                key={day.key}
                type="button"
                disabled={isDisabled}
                className={`${day.key === selectedDate ? "active" : ""} ${missedDateSet.has(day.key) ? "missed" : ""}`}
                onClick={() => selectDate(day.key)}
              >
                <span>{day.weekday}</span>
                <strong>{day.day}</strong>
              </button>
            );
          })}
        </div>
      </div>

      <TaskMetrics metrics={metrics} />

      {statusMessage ? (
        <div className={`employee-task-message ${statusMessage.type}`}>
          <AppIcon name="infoCircle" />
          <span>{statusMessage.text}</span>
        </div>
      ) : null}

      {missedTasks.length ? (
        <div className="employee-task-message warning">
          <AppIcon name="infoCircle" />
          <span>
            {missedTasks.length} missed task{missedTasks.length > 1 ? "s" : ""} from{" "}
            {missedDates.slice(0, 3).map(formatTaskDate).join(", ")}.
          </span>
        </div>
      ) : null}

      <div className="employee-task-table-card">
        <div className="employee-task-table-header">
          <span>Task Details</span>
          <span>Assigned By</span>
          <span>Assigned To</span>
          <span>Priority</span>
          <span>Est. Time</span>
          <span>Due Date</span>
          <span>Status</span>
        </div>

        {isLoading ? <div className="employee-task-empty">Loading tasks...</div> : null}
        {!isLoading && !visibleTasks.length ? (
          <div className="employee-task-empty">No tasks found for this date.</div>
        ) : null}

        {!isLoading &&
          visibleTasks.map((task) => (
            <button
              className="employee-task-table-row"
              key={task._id}
              type="button"
              onClick={() => setSelectedTask(task)}
            >
              <div className="employee-task-title-cell">
                <strong>{task.title}</strong>
                <span>{task.details}</span>
              </div>
              <PersonCell person={task.assignedBy} />
              <PersonCell person={task.assignedTo} />
              <span className={`employee-task-pill priority-${task.priority}`}>
                {formatTaskLabel(task.priority)}
              </span>
              <span>{task.estimatedTimeLabel}</span>
              <span>
                {formatTaskDate(task.dueDate)}
                <small className={task.effectiveStatus === "overdue" ? "danger" : ""}>
                  {task.effectiveStatus === "overdue" ? "Overdue" : task.dueDate === todayKey() ? "Today" : ""}
                </small>
              </span>
              {activeTab === "mine" ? (
                <select
                  value={task.status}
                  disabled={isUpdatingTaskId === task._id || task.status === "completed"}
                  onClick={(event) => event.stopPropagation()}
                  onChange={(event) => updateTaskStatus(task, event.target.value)}
                >
                  <option value="todo">To Do</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              ) : (
                <span className={`employee-task-pill status-${task.effectiveStatus}`}>
                  {formatTaskLabel(task.effectiveStatus)}
                </span>
              )}
            </button>
          ))}
      </div>

      {selectedTask ? (
        <TaskDetailsModal
          task={selectedTask}
          canUpdate={activeTab === "mine" && selectedTask.status !== "completed"}
          onClose={() => setSelectedTask(null)}
          onUpdateStatus={(status) => updateTaskStatus(selectedTask, status)}
          isUpdating={isUpdatingTaskId === selectedTask._id}
        />
      ) : null}

      {isAssignModalOpen ? (
        <TaskAssignModal
          taskForm={taskForm}
          setTaskForm={setTaskForm}
          employees={taskPermission.assignableEmployees || []}
          dateWindow={dateWindow}
          onClose={closeAssignModal}
          onSubmit={submitAssignedTask}
          isSaving={isSavingTask}
        />
      ) : null}
    </section>
  );
}

function TaskMetrics({ metrics }) {
  const cards = [
    ["Total Tasks", metrics.totalTasks || 0, "calendar", "blue"],
    ["Estimated Time", metrics.estimatedTimeLabel || "0h 00m", "clock", "amber"],
    ["Completed", metrics.completed || 0, "checkCircle", "green"],
    ["In Progress", metrics.inProgress || 0, "clock", "purple"],
    ["Overdue", metrics.overdue || 0, "infoCircle", "red"],
  ];

  return (
    <div className="employee-task-metric-grid">
      {cards.map(([label, value, icon, tone]) => (
        <article className="employee-task-metric-card" key={label}>
          <span className={`employee-task-metric-icon ${tone}`}>
            <AppIcon name={icon} />
          </span>
          <div>
            <small>{label}</small>
            <strong>{value}</strong>
          </div>
        </article>
      ))}
    </div>
  );
}

function PersonCell({ person }) {
  const presenceMeta = getPresenceStatusMeta(resolveEmployeeProfileStatus(person));

  return (
    <div className="employee-task-person-cell">
      <div className="employee-presence-avatar-wrap compact">
        {person?.profilePhotoUrl ? (
          <img src={person.profilePhotoUrl} alt="" />
        ) : (
          <span>{String(person?.fullName || "?").slice(0, 1).toUpperCase()}</span>
        )}
        {person?.userType === "employee" ? (
          <span
            className={`employee-presence-badge compact ${presenceMeta.className}`}
            title={presenceMeta.label}
            aria-label={presenceMeta.label}
          >
            {presenceMeta.icon ? <AppIcon name={presenceMeta.icon} /> : <span className="employee-presence-dot" />}
          </span>
        ) : null}
      </div>
      <div>
        <strong>{person?.fullName || "—"}</strong>
        <small>{person?.designation || person?.department || "—"}</small>
      </div>
    </div>
  );
}

function TaskDetailsModal({ task, canUpdate, onClose, onUpdateStatus, isUpdating }) {
  return (
    <div className="employee-modal-overlay" role="presentation" onClick={onClose}>
      <section className="employee-regularization-modal employee-task-detail-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <div className="employee-task-detail-head">
          <div>
            <h3>{task.title}</h3>
            <p>{task.project || "General"} • {formatTaskDate(task.dueDate)}</p>
          </div>
          <button type="button" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>
        <p className="employee-task-detail-copy">{task.details}</p>
        <div className="employee-task-detail-grid">
          <div><span>Assigned By</span><strong>{task.assignedBy?.fullName}</strong></div>
          <div><span>Assigned To</span><strong>{task.assignedTo?.fullName}</strong></div>
          <div><span>Priority</span><strong>{formatTaskLabel(task.priority)}</strong></div>
          <div><span>Estimated Time</span><strong>{task.estimatedTimeLabel}</strong></div>
          <div><span>Status</span><strong>{formatTaskLabel(task.effectiveStatus)}</strong></div>
          <div><span>Started At</span><strong>{task.startedAt ? new Date(task.startedAt).toLocaleString("en-IN") : "—"}</strong></div>
          <div><span>Completed At</span><strong>{task.completedAt ? new Date(task.completedAt).toLocaleString("en-IN") : "—"}</strong></div>
        </div>
        {canUpdate ? (
          <div className="employee-task-detail-actions">
            <button className="secondary-button" type="button" disabled={isUpdating} onClick={() => onUpdateStatus("in_progress")}>
              Start Task
            </button>
            <button className="primary-button" type="button" disabled={isUpdating} onClick={() => onUpdateStatus("completed")}>
              Complete Task
            </button>
          </div>
        ) : null}
      </section>
    </div>
  );
}

function TaskAssignModal({
  taskForm,
  setTaskForm,
  employees,
  dateWindow,
  onClose,
  onSubmit,
  isSaving,
}) {
  return (
    <div className="employee-modal-overlay" role="presentation" onClick={onClose}>
      <section className="employee-regularization-modal employee-task-assign-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <h3>Assign Task</h3>
        <p className="employee-modal-copy">Create a task for an allowed employee.</p>
        <form className="employee-task-form-grid" onSubmit={onSubmit}>
          <label className="form-field">
            <span>Task Title</span>
            <div className="field-shell">
              <input value={taskForm.title} onChange={(event) => setTaskForm((current) => ({ ...current, title: event.target.value }))} placeholder="Enter task title" />
            </div>
          </label>
          <label className="form-field">
            <span>Project</span>
            <div className="field-shell">
              <input value={taskForm.project} onChange={(event) => setTaskForm((current) => ({ ...current, project: event.target.value }))} placeholder="Project name" />
            </div>
          </label>
          <label className="form-field employee-task-form-wide">
            <span>Proper Details</span>
            <textarea value={taskForm.details} onChange={(event) => setTaskForm((current) => ({ ...current, details: event.target.value }))} placeholder="Write task details" />
          </label>
          <label className="form-field">
            <span>Assign To</span>
            <div className="field-shell">
              <select value={taskForm.assignedTo} onChange={(event) => setTaskForm((current) => ({ ...current, assignedTo: event.target.value }))}>
                <option value="">Select employee</option>
                {employees.map((employee) => (
                  <option key={employee._id} value={employee._id}>{employee.fullName}</option>
                ))}
              </select>
            </div>
          </label>
          <label className="form-field">
            <span>Due Date</span>
            <div className="field-shell">
              <input type="date" min={dateWindow.minDate} max={dateWindow.maxDate} value={taskForm.dueDate} onChange={(event) => setTaskForm((current) => ({ ...current, dueDate: event.target.value }))} />
            </div>
          </label>
          <label className="form-field">
            <span>Priority</span>
            <div className="field-shell">
              <select value={taskForm.priority} onChange={(event) => setTaskForm((current) => ({ ...current, priority: event.target.value }))}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </label>
          <div className="employee-task-estimate-grid">
            <label className="form-field">
              <span>Hours</span>
              <div className="field-shell">
                <input type="number" min="0" value={taskForm.estimatedHours} onChange={(event) => setTaskForm((current) => ({ ...current, estimatedHours: event.target.value }))} />
              </div>
            </label>
            <label className="form-field">
              <span>Minutes</span>
              <div className="field-shell">
                <input type="number" min="0" max="59" value={taskForm.estimatedExtraMinutes} onChange={(event) => setTaskForm((current) => ({ ...current, estimatedExtraMinutes: event.target.value }))} />
              </div>
            </label>
          </div>
          <div className="employee-task-form-actions">
            <button className="secondary-button" type="button" onClick={onClose}>Cancel</button>
            <button className="primary-button" type="submit">{isSaving ? "Saving..." : "Assign Task"}</button>
          </div>
        </form>
      </section>
    </div>
  );
}
